import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Link,
  Divider,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import { useNavigate } from 'react-router-dom';
import { isRTL } from '../i18n';

const LegalPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Check if current language is RTL
  const rtl = isRTL();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => navigate(-1)} 
          sx={{ mr: rtl ? 0 : 2, ml: rtl ? 2 : 0 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Legal Information
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Important:</strong> Please review our legal documents to understand your rights and responsibilities when using Tulidu.
        </Typography>
      </Alert>

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        {/* Privacy Policy Card */}
        <Card sx={{ height: 'fit-content' }}>
          <CardContent>
            <Typography variant="h5" component="h2" gutterBottom>
              🔒 Privacy Policy
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Learn how we collect, use, and protect your personal information and portfolio data.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Key Points:</strong>
            </Typography>
            <ul>
              <li>We use read-only access to your wallets</li>
              <li>We never request private keys or seed phrases</li>
              <li>Your cryptocurrency assets remain in your control</li>
              <li>We implement security measures to protect your data</li>
            </ul>
            <Box sx={{ mt: 2 }}>
              <Link 
                href="/privacy-policy.html" 
                target="_blank" 
                rel="noopener noreferrer"
                variant="button"
                sx={{ textDecoration: 'none' }}
              >
                Read Full Privacy Policy
              </Link>
            </Box>
          </CardContent>
        </Card>

        {/* Terms of Service Card */}
        <Card sx={{ height: 'fit-content' }}>
          <CardContent>
            <Typography variant="h5" component="h2" gutterBottom>
              📋 Terms of Service
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Understand the terms and conditions for using our cryptocurrency portfolio management platform.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Key Points:</strong>
            </Typography>
            <ul>
              <li>Tulidu is not a financial institution</li>
              <li>We do not provide investment advice</li>
              <li>Cryptocurrency investments carry significant risk</li>
              <li>You are responsible for your investment decisions</li>
            </ul>
            <Box sx={{ mt: 2 }}>
              <Link 
                href="/terms-of-service.html" 
                target="_blank" 
                rel="noopener noreferrer"
                variant="button"
                sx={{ textDecoration: 'none' }}
              >
                Read Full Terms of Service
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Risk Disclaimer */}
      <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            ⚠️ Cryptocurrency Risk Disclaimer
          </Typography>
          <Typography variant="body2">
            <strong>Important Warning:</strong> Cryptocurrency investments involve substantial risk of loss and are not suitable for all investors. 
            The value of cryptocurrencies can fluctuate dramatically and you may lose some or all of your investment. 
            Past performance does not guarantee future results. Always conduct your own research and consult with a qualified financial advisor before making investment decisions.
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          For questions about our legal documents, please contact us at{' '}
          <Link href="mailto:legal@tulidu.com" color="primary">
            legal@tulidu.com
          </Link>
        </Typography>
      </Box>
    </Container>
  );
};

export default LegalPage;
